import { useState, useMemo } from 'react';
import { Upload, AlertCircle, CheckCircle2, Eye, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { useImportFamilies } from './useImportFamilies';
import ImportPreviewTable, { getEditedTsvFromData } from './ImportPreviewTable';

type RowData = Record<string, string>;

interface ParseResult {
  headers: string[];
  rows: RowData[];
}

function parseTsvForPreview(tsvContent: string): ParseResult {
  const lines = tsvContent.trim().split('\n');
  if (lines.length === 0) return { headers: [], rows: [] };

  // Get all headers (including empty ones for alignment)
  const allHeaders = lines[0].split('\t').map(h => h.trim());
  // Filter to only non-empty headers for display
  const headers = allHeaders.filter(h => h);

  const rows: RowData[] = [];

  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const values = lines[i].split('\t').map(v => {
      let val = v.trim();
      if (val.startsWith('"') && val.endsWith('"')) {
        val = val.slice(1, -1).replace(/""/g, '"');
      }
      return val;
    });

    // Pad values to match allHeaders length
    while (values.length < allHeaders.length) {
      values.push('');
    }

    const row: RowData = {};
    // Map values to headers, skipping empty header columns
    allHeaders.forEach((header, idx) => {
      if (header) {
        row[header] = values[idx] || '';
      }
    });
    rows.push(row);
  }

  return { headers, rows };
}

interface ImportResult {
  importedCount: number;
  errors: string[];
}

interface ImportFamiliesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string | undefined;
  onSuccess?: () => void;
}

function ImportFamiliesDialog({ open, onOpenChange, schoolId, onSuccess }: ImportFamiliesDialogProps) {
  const [familyTsv, setFamilyTsv] = useState('');
  const [childrenTsv, setChildrenTsv] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('input');
  const [editedFamilyData, setEditedFamilyData] = useState<RowData[] | null>(null);
  const [editedChildrenData, setEditedChildrenData] = useState<RowData[] | null>(null);

  const [importFamilies] = useImportFamilies();

  // Extract family IDs from family TSV
  const familyIds = useMemo(() => {
    if (!familyTsv.trim()) return [] as string[];
    const { rows } = parseTsvForPreview(familyTsv);
    const ids = new Set<string>();
    rows.forEach(row => {
      const id = row['ID'] || row['IID'];
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }, [familyTsv]);

  // Parse preview data
  const familyPreview = useMemo(() => parseTsvForPreview(familyTsv), [familyTsv]);
  const childrenPreview = useMemo(() => parseTsvForPreview(childrenTsv), [childrenTsv]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'family' | 'children') => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (type === 'family') {
        setFamilyTsv(content);
      } else {
        setChildrenTsv(content);
      }
    };
    reader.readAsText(file);
  };

  const handlePaste = (e: React.ClipboardEvent, type: 'family' | 'children') => {
    const content = e.clipboardData.getData('text');
    if (type === 'family') {
      setFamilyTsv(content);
    } else {
      setChildrenTsv(content);
    }
  };

  // Validate data before import
  const validateImportData = (familyData: RowData[], childrenData: RowData[], availableFamilyIds: string[]) => {
    const errors: string[] = [];

    // Validate families
    familyData.forEach((row, idx) => {
      const rowNum = idx + 2; // +2 because row 1 is header, and we're 0-indexed
      const familyId = row['ID'] || row['IID'];

      if (!familyId || !familyId.trim()) {
        errors.push(`Family row ${rowNum}: Missing Family ID (ID or IID column required)`);
      }

      if (!row['Family Name'] || !row['Family Name'].trim()) {
        errors.push(`Family row ${rowNum}: Missing Family Name`);
      }
    });

    // Validate children
    childrenData.forEach((row, idx) => {
      const rowNum = idx + 2; // +2 because row 1 is header, and we're 0-indexed
      const familyId = row['Family ID'];

      if (!familyId || !familyId.trim()) {
        errors.push(`Child row ${rowNum}: Missing Family ID`);
      } else if (!availableFamilyIds.includes(familyId)) {
        errors.push(`Child row ${rowNum}: Family ID '${familyId}' not found in family import`);
      }

      if (!row['First Name'] || !row['First Name'].trim()) {
        errors.push(`Child row ${rowNum}: Missing First Name`);
      }

      if (!row['Last Name'] || !row['Last Name'].trim()) {
        errors.push(`Child row ${rowNum}: Missing Last Name`);
      }

      // Validate birthdate format if provided
      if (row['Birthdate'] && row['Birthdate'].trim()) {
        const dateStr = row['Birthdate'].trim();
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          errors.push(`Child row ${rowNum}: Invalid birthdate format '${dateStr}' (expected YYYY-MM-DD)`);
        }
      }
    });

    return errors;
  };

  const handleImport = async () => {
    if (!familyTsv.trim() || !childrenTsv.trim()) {
      setError('Both Family TSV and Children TSV are required');
      return;
    }

    setIsImporting(true);
    setError(null);
    setResult(null);

    // Use edited data if available, otherwise use original TSV
    let finalFamilyTsv = familyTsv.trim();
    let finalChildrenTsv = childrenTsv.trim();

    // Get data for validation
    let familyData = familyPreview.rows;
    let childrenData = childrenPreview.rows;

    if (editedFamilyData && editedFamilyData.length > 0 && familyPreview.headers.length > 0) {
      finalFamilyTsv = getEditedTsvFromData(familyPreview.headers, editedFamilyData);
      familyData = editedFamilyData;
    }

    if (editedChildrenData && editedChildrenData.length > 0 && childrenPreview.headers.length > 0) {
      finalChildrenTsv = getEditedTsvFromData(childrenPreview.headers, editedChildrenData);
      childrenData = editedChildrenData;
    }

    // Validate before submitting
    const validationErrors = validateImportData(familyData, childrenData, familyIds);
    if (validationErrors.length > 0) {
      setError(`Validation errors found. Please fix before importing:\n${validationErrors.join('\n')}`);
      setIsImporting(false);
      return;
    }

    try {
      const { data } = await importFamilies({
        variables: {
          input: {
            schoolId: schoolId!,
            familyTsv: finalFamilyTsv,
            childrenTsv: finalChildrenTsv,
          },
        },
      });

      if (data?.importFamilies?.userErrors?.length) {
        setError(data.importFamilies.userErrors.map((e: any) => e.message).join(', '));
      } else if (data?.importFamilies?.errors?.length && data.importFamilies.errors.length > 0) {
        // Backend validation errors - rollback should have happened
        setError(`Import failed with errors:\n${data.importFamilies.errors.join('\n')}`);
      } else {
        setResult({
          importedCount: data?.importFamilies?.importedCount || 0,
          errors: [],
        });

        // Only call onSuccess if there are no errors
        setTimeout(() => {
          onSuccess?.();
          handleClose();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import families');
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setFamilyTsv('');
    setChildrenTsv('');
    setError(null);
    setResult(null);
    setActiveTab('input');
    setEditedFamilyData(null);
    setEditedChildrenData(null);
    onOpenChange(false);
  };

  const hasPreviewData = familyPreview.rows.length > 0 || childrenPreview.rows.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Families</DialogTitle>
          <DialogDescription>
            Upload or paste TSV files for families and children. Review and edit the data before importing.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="input">
              <FileText className="h-4 w-4 mr-2" />
              Input
            </TabsTrigger>
            <TabsTrigger value="preview" disabled={!hasPreviewData}>
              <Eye className="h-4 w-4 mr-2" />
              Preview & Edit
            </TabsTrigger>
          </TabsList>

          <TabsContent value="input" className="space-y-6 py-4">
            {/* Family TSV */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="family-tsv">Family TSV</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    id="family-file-upload"
                    accept=".tsv,.txt"
                    onChange={(e) => handleFileUpload(e, 'family')}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('family-file-upload')?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload File
                  </Button>
                </div>
              </div>
              <Textarea
                id="family-tsv"
                placeholder="Paste Family TSV content here or upload a file..."
                value={familyTsv}
                onChange={(e) => setFamilyTsv(e.target.value)}
                onPaste={(e) => handlePaste(e, 'family')}
                rows={8}
                className="font-mono text-sm"
              />
            </div>

            {/* Children TSV */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="children-tsv">Children TSV</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    id="children-file-upload"
                    accept=".tsv,.txt"
                    onChange={(e) => handleFileUpload(e, 'children')}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('children-file-upload')?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload File
                  </Button>
                </div>
              </div>
              <Textarea
                id="children-tsv"
                placeholder="Paste Children TSV content here or upload a file..."
                value={childrenTsv}
                onChange={(e) => setChildrenTsv(e.target.value)}
                onPaste={(e) => handlePaste(e, 'children')}
                rows={8}
                className="font-mono text-sm"
              />
            </div>

            {hasPreviewData && (
              <div className="p-4 rounded-md bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-800">
                  Data detected! Switch to the <strong>Preview & Edit</strong> tab to review and edit before importing.
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="preview" className="space-y-6 py-4">
            {familyPreview.rows.length > 0 && (
              <ImportPreviewTable
                tsvContent={familyTsv}
                title="Families"
                onDataChange={setEditedFamilyData}
                isChildrenTable={false}
              />
            )}

            {childrenPreview.rows.length > 0 && (
              <ImportPreviewTable
                tsvContent={childrenTsv}
                title="Children"
                familyIds={familyIds}
                onDataChange={setEditedChildrenData}
                isChildrenTable={true}
              />
            )}

            {!hasPreviewData && (
              <div className="text-sm text-muted-foreground p-4 text-center">
                No data to preview. Add TSV content in the Input tab first.
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Error Display */}
        {error && (
          <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          </div>
        )}

        {/* Success/Result Display */}
        {result && (
          <div className={`p-4 rounded-md border ${result.errors.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-start gap-2">
              <CheckCircle2 className={`h-4 w-4 mt-0.5 ${result.errors.length > 0 ? 'text-yellow-600' : 'text-green-600'}`} />
              <div className="flex-1 space-y-2">
                <p className={`text-sm ${result.errors.length > 0 ? 'text-yellow-800' : 'text-green-800'}`}>
                  Successfully imported <strong>{result.importedCount}</strong> families.
                </p>
                {result.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-sm mb-1 text-yellow-800">Errors:</p>
                    <ul className="list-disc list-inside text-sm space-y-1 text-yellow-700">
                      {result.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isImporting}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={isImporting || !familyTsv.trim() || !childrenTsv.trim()}>
            {isImporting ? 'Importing...' : 'Import Families'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ImportFamiliesDialog;
